import React, { useState } from 'react';
import { X, Edit, Trash2 } from 'lucide-react';
import { RyanSubdeckGroupData, RyanTab } from '../types/ryan.types';
import { getRyanIconComponent } from '../utils/ryanGroupIcons';

interface RyanManageGroupsModalProps {
  spaceId: string;
  groups: RyanSubdeckGroupData[];
  tabs: RyanTab[];
  activeTabId: string;
  onClose: () => void;
  onEdit: (group: RyanSubdeckGroupData) => void;
  onDelete: (group: RyanSubdeckGroupData) => void;
  onSuccess: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const RyanManageGroupsModal: React.FC<RyanManageGroupsModalProps> = ({
  spaceId, groups, tabs, activeTabId,
  onClose, onEdit, onDelete, onSuccess, onToast,
}) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const getTabName = (tabId: string | null) => {
    if (!tabId) return 'Sem aba';
    return tabs.find(t => t._id === tabId)?.name || 'Aba desconhecida';
  };

  const handleDelete = async (group: RyanSubdeckGroupData) => {
    if (deletingId === group._id) {
      const token = localStorage.getItem('accessToken');
      try {
        const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/groups/${group._id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          onToast('Erro ao excluir grupo', 'error');
          return;
        }
        onToast('Grupo excluído!', 'success');
        onSuccess();
        setDeletingId(null);
      } catch {
        onToast('Erro de conexão', 'error');
      }
    } else {
      setDeletingId(group._id);
    }
  };

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal ryan-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">Gerenciar Grupos</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          {groups.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, fontFamily: 'Inter, sans-serif', textAlign: 'center', padding: '24px 0' }}>
              Nenhum grupo criado ainda.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {groups.map(group => {
                const IconComp = getRyanIconComponent(group.iconName);
                const isConfirmingDelete = deletingId === group._id;
                return (
                  <div
                    key={group._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 14px',
                      background: '#f8faf8',
                      borderRadius: 10,
                      border: '1px solid rgba(13,59,14,0.08)',
                    }}
                  >
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: `${group.color}18`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <IconComp size={18} color={group.color} />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0D3B0E', fontFamily: 'Inter, sans-serif' }}>
                        {group.name}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                        {getTabName(group.tabId)} · {group.subdeckCount || 0} decks
                      </div>
                    </div>

                    {isConfirmingDelete ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12.5, color: '#dc2626', fontFamily: 'Inter, sans-serif' }}>Confirmar?</span>
                        <button
                          className="ryan-modal-save-btn"
                          style={{ background: '#dc2626', padding: '5px 10px', fontSize: 12 }}
                          onClick={() => handleDelete(group)}
                        >Sim</button>
                        <button
                          className="ryan-modal-cancel-btn"
                          style={{ padding: '5px 10px', fontSize: 12 }}
                          onClick={() => setDeletingId(null)}
                        >Não</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="ryan-group-kebab-btn"
                          title="Editar grupo"
                          onClick={() => { onEdit(group); onClose(); }}
                        >
                          <Edit size={15} />
                        </button>
                        <button
                          className="ryan-group-kebab-btn"
                          title="Excluir grupo"
                          style={{ color: '#dc2626' }}
                          onClick={() => handleDelete(group)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default RyanManageGroupsModal;
